import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useVerifyEmailMutation } from '../../../queries/auth';
import AuthShell from '../../../components/auth/AuthShell';
import { ErrorBanner, Head, Submit } from '../../../components/auth/parts';
import { classify } from '../../../components/auth/authErrors';
import useCountdown from '../../../components/auth/useCountdown';
import useAuthTitle from '../../../components/auth/useAuthTitle';

/**
 * Email-verification landing page.
 *
 * DO NOT re-add a `useEffect(() => verify(), [])` here.
 *
 * This page used to fire the verification POST on mount, with no interaction.
 * Corporate mail security (Microsoft Defender Safe Links and friends) opens
 * every URL in an inbound message inside a headless browser to detonate it —
 * that renders this route, React mounts, the effect fires, and the account is
 * verified. Production logs for 2026-07/08 show exactly that: the verify hits
 * arrived from Microsoft and Verizon mail infrastructure, never from the IPs
 * that had performed the signups.
 *
 * The effect was therefore doing the bot's work for it: it flipped
 * company.status to ACTIVE, minted a 7-day trial subscription via
 * UserObserver::updated, and sent a second unsolicited email to a harvested
 * address. A scanner will click nothing, so an explicit user gesture is the
 * whole fix.
 */
function VerifyEmailProcess () {
  const { payload } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const S = (k) => t(`authPage.confirm.${k}`);

  const [view, setView] = useState('idle'); // idle | success | used | invalid
  const [banner, setBanner] = useState(null); // rate | offline
  const cd = useCountdown();
  const timer = useRef(null);
  const verifyEmail = useVerifyEmailMutation({ inline: true });
  useAuthTitle('confirm');

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { if (banner === 'rate' && cd.left === 0) setBanner(null); }, [banner, cd.left]);

  // The one and only trigger is this explicit click.
  const onSubmit = async (e) => {
    e.preventDefault();
    if (verifyEmail.isLoading || (banner === 'rate' && cd.left > 0)) return;
    setBanner(null);
    try {
      const res = await verifyEmail.mutateAsync({ payload });
      if (res?.already_verified) { setView('used'); return; }
      setView('success');
      timer.current = setTimeout(() => navigate('/dashboard'), 1400);
    } catch (err) {
      const info = classify(err);
      if (info.kind === 'offline') setBanner('offline');
      else if (info.kind === 'rate') { cd.start(info.retryAfter); setBanner('rate'); }
      else setView('invalid'); // expired / bad payload (the API 500s on a mangled one) / unknown user
    }
  };

  if (view === 'success') {
    return (
      <AuthShell>
        <Head icon="ok" tone="good" title={S('okH')} text={S('okP')}><div className="ppa-bar"><i /></div></Head>
      </AuthShell>
    );
  }
  if (view === 'used') {
    return (
      <AuthShell>
        <Head icon="ok" tone="good" title={S('usedH')} text={S('usedP')} />
        <button className="ppa-btn" type="button" onClick={() => navigate('/login')}>{S('login')}</button>
      </AuthShell>
    );
  }
  if (view === 'invalid') {
    return (
      <AuthShell>
        <Head icon="broken" tone="crit" title={S('invH')} text={S('invP')} />
        <button className="ppa-btn" type="button" onClick={() => navigate('/login')}>{S('login')}</button>
      </AuthShell>
    );
  }
  return (
    <AuthShell>
      <Head icon="mail" title={S('h')} text={S('p')} />
      <form noValidate onSubmit={onSubmit}>
        {banner && <ErrorBanner kind={banner} left={cd.left} />}
        <Submit busy={verifyEmail.isLoading} busyLabel={S('busy')} disabled={banner === 'rate' && cd.left > 0}>{S('btn')}</Submit>
      </form>
    </AuthShell>
  );
}

export default VerifyEmailProcess;
